/**
 * @number-flow causes issues when rendered serverside in Vite, only importing types here
 * and suffixing with `Type` to avoid confusion with the import.
 *
 * @number-flow will be dynamically imported in the component AnimatedDigits
 */
import type NumberFlowType from '@number-flow/react';
import type { NumberFlowElement as NumberFlowElementType } from '@number-flow/react';
import type { ReactElement } from 'react';
import React, { memo, useState } from 'react';

import { useMountEffect } from '@infinex/ui-utils/react';

import { arePropsEqual, delay } from '../../utils/index';

type NumberFlowType = typeof NumberFlowType;

interface AnimatedDigitsProps {
  withAnimation?: boolean;
  children: string;
  className?: string;
  style?: React.CSSProperties;
  prefix?: string;
  suffix?: string;
}

export const AnimatedDigits = memo(AnimatedDigitsComponent, arePropsEqual);

function AnimatedDigitsComponent({
  withAnimation = false,
  children,
  className,
  style,
  prefix,
  suffix,
}: AnimatedDigitsProps) {
  const [{ NumberFlow, NumberFlowElement }, setNumberFlow] = useState<{
    NumberFlow?: NumberFlowType;
    NumberFlowElement?: NumberFlowElementType;
  }>({});

  useMountEffect(() => {
    // The import resolves asynchronously, so the component may unmount first;
    // skip the state update in that case.
    let cancelled = false;

    // Dynamically import NumberFlow to avoid hydration issues
    void import('@number-flow/react')
      .then(async (m) => {
        // ISSUE: https://linear.app/infinex-app/issue/ENG-4282/fix-flickering-issue-when-switching-views-on-mobile
        // Wait 50ms to prevent Flash of Unstyled Content on Safari mobile where NumberFlow briefly shows
        // all digits (0123456789) before CSS styles are applied to hide inactive ones.
        // This delay ensures NumberFlow's Shadow DOM and internal CSS are fully loaded
        // before the component renders, preventing the flash of unstyled content.
        // we got another solution, but let's see if 50ms works
        // https://github.com/infinex-xyz/platform/pull/7581
        await delay(50);
        return m;
      })
      .then((m) => {
        if (cancelled) return;
        setNumberFlow({
          NumberFlow: m.default as NumberFlowType,
          NumberFlowElement:
            m.NumberFlowElement as unknown as NumberFlowElementType,
        });
      });

    return () => {
      cancelled = true;
    };
  });

  return (
    <span className={className} style={style}>
      {withAnimation && NumberFlow && NumberFlowElement ? (
        renderWithNumberFlow(
          children,
          prefix,
          suffix,
          NumberFlow,
          NumberFlowElement
        )
      ) : (
        <>
          {prefix}
          {children}
          {suffix}
        </>
      )}
    </span>
  );
}

function isCharAValidDigit(char: string) {
  return /^[0-9]$/.test(char);
}

/**
 * This function make sure that all the content will be rendered inside the NumberFlow, which can make sure the line height is consistent.
 * The NumberFlow component's value only accepts number, so we need to convert the string to number. But for the value which is not a digit,
 * we need to keep it as a string. Previously we render it as as span, but the line height is not consistent.
 *
 * This function use the trick to add the non-digit characters to the prefix, so that the NumberFlow can render them as a string.
 * @param str
 * @param prefix
 * @param suffix
 * @param NumberFlow
 * @param NumberFlowElement
 * @returns
 */
function renderWithNumberFlow(
  str: string,
  prefix: string | undefined,
  suffix: string | undefined,
  NumberFlow: NumberFlowType,
  NumberFlowElement: NumberFlowElementType
) {
  prefix = prefix || '';
  const nodes: ReactElement[] = [];

  str.split('').forEach((v, i) => {
    if (!isCharAValidDigit(v)) {
      // if it's not a digit, add it to the prefix so that it can be added to the NumberFlow
      prefix += v;
    } else {
      nodes.push(
        <NumberFlow
          key={i}
          prefix={prefix || undefined}
          suffix={i === str.length - 1 ? suffix : undefined}
          value={parseInt(v, 10)}
          transformTiming={{
            // @ts-expect-error TS Linting doesn't like accessing static class properties of dynamically imported modules
            ...NumberFlowElement.defaultProps['transformTiming']!,
            duration: 300, // default is 900, which seems too long
          }}
        />
      );
      // reset the prefix for the next NumberFlow, since it's already added to the prefix in this turn
      prefix = '';
    }
  });

  return nodes;
}
